import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getNewbornInitialAssessmentRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface NewbornInitialAssessmentRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  isViewOnly: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
}

interface AssessmentItem {
  label: string;
  value: string;
}

export default function NewbornInitialAssessmentRow({
  time,
  observation,
  showNumbersOnly,
  isViewOnly,
  showPreviousVersions = true,
  onRefresh,
}: NewbornInitialAssessmentRowProps) {
  const { notes, hasAnswers, hasNotes } = useMemo(
    () =>
      observation
        ? getNewbornInitialAssessmentRowValues(observation)
        : {
            notes: undefined,
            hasAnswers: false,
            hasNotes: false,
          },
    [observation],
  );
  const assessmentItems = useMemo<AssessmentItem[]>(() => {
    if (!observation?.component) return [];

    const items: AssessmentItem[] = [];

    observation.component.forEach((component) => {
      const label = component.code?.coding?.[0]?.display ?? component.code?.text ?? "Assessment";
      const value = component.valueCodeableConcept?.coding?.[0]?.display ?? component.valueString;

      if (!value) return;

      items.push({
        label,
        value,
      });
    });

    return items;
  }, [observation]);

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Initial Assessment:</TextWrapper>
            <List fz="sm" withPadding>
              {assessmentItems.map(
                (item, index) =>
                  item.value && (
                    <List.Item key={`${item.label}-${item.value}-${index}`}>
                      <Group gap="xs">
                        <TextWrapper size="sm" variant="bold">
                          {item.label}:
                        </TextWrapper>
                        <TextWrapper size="sm">{item.value}</TextWrapper>
                      </Group>
                    </List.Item>
                  ),
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Assessment Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={NewbornInitialAssessmentRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
